/**
 * PATH: packages/email-hub/src/feature/hooks/useGlobalMarkupShortcut.ts
 * FILE_ID: EMAIL-HUB-HOOK-MARKUP-SHORTCUT-001
 * PURPOSE: Bind a global keyboard shortcut (Cmd/Ctrl + Shift + M)
 *          that reads the system clipboard, looks for an image,
 *          and opens the markup drawer pre-loaded with it.
 * WHY:     Operator request 2026-05-09: "create a short cut key
 *          to open copied clipboard in a right drawer sense so I
 *          can mark up the image if required". The intended
 *          workflow is: take a screenshot (OS-level), Cmd+Shift+M,
 *          annotate, send / paste.
 *          Choice of Cmd+Shift+M:
 *            - "M" for Mark / Markup (mnemonic)
 *            - Shift forces a deliberate combo (Cmd+M is OS-level
 *              minimise on macOS — never collide)
 *            - Doesn't conflict with browser defaults (Cmd+Shift+M
 *              on Chrome opens Profile menu but only when the
 *              browser chrome has focus, not the page)
 * FLOW:
 *   keydown ⌘⇧M
 *     ↓
 *   readClipboardImage() → blob URL → onOpen(url)
 *     ↓
 *   parent renders MarkupDrawer with src=url
 * RELATED:
 *   - components/MarkupDrawer.tsx
 *   - lib/imageClipboard.ts
 */
import { useEffect } from 'react'
import { readClipboardImage } from '../lib/imageClipboard'

export interface UseGlobalMarkupShortcutOptions {
  /** Called with a transient ``blob:`` URL pointing at the image
   *  that was on the clipboard. Caller is responsible for revoking
   *  the URL when the drawer closes. ``onMissing`` fires when the
   *  shortcut was pressed but the clipboard had nothing image-
   *  shaped — UI can flash a toast. */
  onOpen: (src: string) => void
  onMissing?: () => void
  /** Default ``true``. Disable temporarily if a modal already owns
   *  the keyboard (e.g. compose modal where Cmd+Shift+M might mean
   *  something else). */
  enabled?: boolean
}

export function useGlobalMarkupShortcut({
  onOpen, onMissing, enabled = true,
}: UseGlobalMarkupShortcutOptions): void {
  useEffect(() => {
    if (!enabled) return
    const handler = async (e: KeyboardEvent) => {
      if (!isMarkupShortcut(e)) return
      if (isInsideEditableField(e.target)) return
      e.preventDefault()
      const img = await readClipboardImage()
      if (!img) {
        onMissing?.()
        return
      }
      const url = URL.createObjectURL(img.blob)
      onOpen(url)
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [enabled, onOpen, onMissing])
}

/** Cmd+Shift+M (mac) or Ctrl+Shift+M (Windows/Linux). We accept
 *  either platform combo so a Linux operator on a Mac keyboard
 *  hasn't lost their muscle memory. We deliberately exclude the
 *  Alt key — many IMEs map dead-keys onto Alt. */
export function isMarkupShortcut(e: KeyboardEvent): boolean {
  if (!e.shiftKey) return false
  if (!(e.metaKey || e.ctrlKey)) return false
  if (e.altKey) return false
  return e.key === 'M' || e.key === 'm'
}

/** Skip the shortcut when the operator is typing in a form field
 *  — they're more likely to be typing literal "M" than wanting to
 *  invoke markup. We still fire if they're focused on a button or
 *  link. */
function isInsideEditableField(target: EventTarget | null): boolean {
  if (!target || !(target instanceof HTMLElement)) return false
  const tag = target.tagName
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true
  if (target.isContentEditable) return true
  return false
}
